import { PropsWithChildren, ReactElement } from "react";

import { css } from "@compiled/react";
import { defineMessage, useIntl } from "react-intl";

import { token } from "@atlaskit/tokens";

import Link from "@/components/wpl/link/src";
import { useLocalizedHref } from "@/libraries/web-framework";
import { TypographyStyles } from "@/libraries/wpl/foundations/src";

import { LinkCard, TitleCard } from "./components/card";
import { MotionContainer } from "./components/motion-container";
import { RowGrid } from "./layouts/row-grid";
import { LinkColumn } from "./layouts/subnav-link-columns";
import { useCurrentNav } from "./nav-links-container";

type TeamData = {
    id: string;
    title: ReturnType<typeof defineMessage>;
    content: ReturnType<typeof defineMessage>;
    href: string;
    icon: ReactElement;
};

const icons = {
    software: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                d="M9.18198 17L4.24264 12.0607L9.18198 7.12132L7.06066 5L0 12.0607L7.06066 19.1213L9.18198 17Z"
                fill="#BF63F3"
            />
            <path
                d="M14.5607 7.12136L19.5 12.0607L14.5607 17L16.682 19.1214L23.7427 12.0607L16.682 5.00004L14.5607 7.12136Z"
                fill="#BF63F3"
            />
        </svg>
    ),
    marketing: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M1 5.98982H9.55428V15.0191H9.5543V22.1477H5.27716V15.9698H1V5.98982ZM22.3857 20.96L11.6932 16.6829H11.8043V5.27715H11.6925L22.3857 1V20.96Z"
                fill="#BF63F3"
            />
        </svg>
    ),
    projectManagement: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <g clipPath="url(#clip0_1433_4922)">
                <path
                    fillRule="evenodd"
                    clipRule="evenodd"
                    d="M18.3006 24C21.5346 21.8747 23.666 18.2401 23.666 14.1133C23.666 7.56137 18.2934 2.25 11.666 2.25C5.0386 2.25 -0.333984 7.56137 -0.333984 14.1133C-0.333984 18.2401 1.79747 21.8747 5.03141 24C5.61735 20.9046 8.36496 18.562 11.666 18.562C14.9671 18.562 17.7147 20.9046 18.3006 24ZM11.666 16.3376C14.1513 16.3376 16.166 14.3459 16.166 11.8889C16.166 9.43195 14.1513 7.44019 11.666 7.44019C9.18073 7.44019 7.16602 9.43195 7.16602 11.8889C7.16602 14.3459 9.18073 16.3376 11.666 16.3376Z"
                    fill="#FFA900"
                />
            </g>
            <defs>
                <clipPath id="clip0_1433_4922">
                    <rect width="24" height="24" fill="white" />
                </clipPath>
            </defs>
        </svg>
    ),
    design: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="16"
            height="22"
            viewBox="0 0 16 22"
            fill="none"
        >
            <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M9.33333 0H0V21.3333H16V7.33333L9.33333 0ZM8 2V8.66667H14L8 2Z"
                fill="#FFA900"
            />
        </svg>
    ),
    productManagement: (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
        >
            <path d="M0 3.75L16.5 3.75V3.52726e-06L0 0V3.75Z" fill="#82B536" />
            <path d="M1.5 15.75L18 15.75V12L1.5 12L1.5 15.75Z" fill="#82B536" />
            <path d="M24 21.75L7.5 21.75V18L24 18V21.75Z" fill="#82B536" />
            <path d="M6 9.75L22.5 9.75V6L6 6V9.75Z" fill="#82B536" />
        </svg>
    )
} as const;

function useTeamsData(): Array<TeamData> {
    return [
        {
            id: "teams.software",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.software.title",
                defaultMessage: "Software"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.software.content",
                defaultMessage: "Ship high-quality, innovative software faster"
            }),
            href: useLocalizedHref("/teams/software-development"),
            icon: icons.software
        },
        {
            id: "teams.marketing",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.title",
                defaultMessage: "Marketing"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.marketing.content",
                defaultMessage: "Collaborative strategies for marketing success"
            }),
            href: useLocalizedHref("/teams/marketing"),
            icon: icons.marketing
        },
        {
            id: "teams.project-management",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.projectmanagement.title",
                defaultMessage: "Project management"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.projectmanagement.content",
                defaultMessage:
                    "Plan, track, and optimize projects for smooth delivery"
            }),
            href: "#",
            icon: icons.projectManagement
        },
        {
            id: "teams.design",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.design.title",
                defaultMessage: "Design"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.design.content",
                defaultMessage:
                    "Align on user needs and craft breathtaking experiences"
            }),
            href: "#",
            icon: icons.design
        },
        {
            id: "teams.product-management",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.productmanagement.title",
                defaultMessage: "Product management"
            }),
            content: defineMessage({
                id: "globalnavv2.subnav.teams.productmanagement.content",
                defaultMessage:
                    "Master the product lifecycle, from brainstorm to launch"
            }),
            href: "#",
            icon: icons.productManagement
        }
    ];
}

function useTeamsLinks() {
    return {
        solution: {
            id: "solution",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.solution",
                defaultMessage: "Solution"
            }),
            items: [
                {
                    id: "work-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.work-management",
                        defaultMessage: "Work Management"
                    }),
                    href: useLocalizedHref("/solutions/work-management")
                },
                {
                    id: "it-service-management",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.it-service-management",
                        defaultMessage: "IT Service Management"
                    }),
                    href: useLocalizedHref("/solutions/itsm")
                },
                {
                    id: "agile-devops",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.agile-devops",
                        defaultMessage: "Agile & DevOps"
                    }),
                    href: useLocalizedHref("/solutions/devops")
                }
            ]
        },
        byTeamSize: {
            id: "byTeamSize",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-team-size",
                defaultMessage: "By Team Size"
            }),
            items: [
                {
                    id: "enterprise",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.enterprise",
                        defaultMessage: "Enterprise"
                    }),
                    href: useLocalizedHref("/enterprise")
                },
                {
                    id: "small-business",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.small-business",
                        defaultMessage: "Small Business"
                    }),
                    href: useLocalizedHref("/software/small-business")
                },
                {
                    id: "startup",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.startup",
                        defaultMessage: "Startup"
                    }),
                    href: useLocalizedHref("/software/startups")
                },
                {
                    id: "non-profit",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.non-profit",
                        defaultMessage: "Non-profit"
                    }),
                    href: useLocalizedHref("/teams/nonprofits")
                }
            ]
        },
        byIndustry: {
            id: "byIndustry",
            title: defineMessage({
                id: "globalnavv2.subnav.teams.links.by-industry",
                defaultMessage: "By Industry"
            }),
            items: [
                {
                    id: "retail",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.retail",
                        defaultMessage: "Retail"
                    }),
                    href: useLocalizedHref("/industries/retail")
                },

                {
                    id: "telecommunications",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.telecommunications",
                        defaultMessage: "Telecommunications"
                    }),
                    href: useLocalizedHref("/industries/telecom")
                },
                {
                    id: "professional-services",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.professional-services",
                        defaultMessage: "Professional services"
                    }),
                    href: useLocalizedHref("/industries/professional-services")
                },
                {
                    id: "government",
                    text: defineMessage({
                        id: "globalnavv2.subnav.teams.links.government",
                        defaultMessage: "Government"
                    }),
                    href: useLocalizedHref("/government")
                }
            ]
        }
    } as const;
}

const TeamsGrid = ({ children }: PropsWithChildren) => {
    return (
        <RowGrid
            numCols={3}
            rowGap={token("space.500")}
            columnGap={token("space.400")}
            paddingBlock={token("space.400")}
            paddingInline={token("space.300")}
        >
            {children}
        </RowGrid>
    );
};

export const TeamsSubnav = () => {
    const intl = useIntl();

    const teamsData = useTeamsData();
    const teamsLinks = useTeamsLinks();
    const linkColumns = Object.values(teamsLinks);

    const { direction } = useCurrentNav();

    return (
        <TypographyStyles>
            <MotionContainer direction={direction}>
                <TeamsGrid>
                    {teamsData.map(({ id, title, content, ...rest }) => (
                        <TitleCard
                            key={id}
                            title={intl.formatMessage(title)}
                            content={intl.formatMessage(content)}
                            {...rest}
                        />
                    ))}
                    <LinkCard
                        text={intl.formatMessage({
                            id: "globalnavv2.subnav.teams.allteams",
                            defaultMessage: "See all teams"
                        })}
                        href="#"
                    />
                </TeamsGrid>
                <div css={css({ backgroundColor: "#F8F8F8" })}>
                    <RowGrid
                        numCols={3}
                        paddingBlock={token("space.400")}
                        paddingInline={token("space.400")}
                    >
                        {linkColumns.map((column) => {
                            return (
                                <LinkColumn key={column.id}>
                                    <span
                                        css={css({
                                            "textTransform": "uppercase",
                                            "color": "#101214",
                                            "marginBlock": `0 ${token(
                                                "space.200"
                                            )}`,

                                            "&.h6": {
                                                fontSize: "0.75rem"
                                            }
                                        })}
                                        className="h6"
                                    >
                                        {intl.formatMessage(column.title)}
                                    </span>
                                    {column.items.map((link) => {
                                        return (
                                            <div
                                                key={link.id}
                                                css={css({
                                                    a: {
                                                        fontWeight: 400,
                                                        fontSize: "0.875rem",
                                                        marginBlockEnd:
                                                            "0.5rem",
                                                        lineHeight: "1.5rem",
                                                        color: "#101214"
                                                    }
                                                })}
                                            >
                                                <Link
                                                    type="anchor"
                                                    href={link.href}
                                                    text={intl.formatMessage(
                                                        link.text
                                                    )}
                                                />
                                            </div>
                                        );
                                    })}
                                </LinkColumn>
                            );
                        })}
                    </RowGrid>
                </div>
            </MotionContainer>
        </TypographyStyles>
    );
};
